import React, { useState, useEffect } from 'react';
import { useSystemProfile } from '../utils/systemProfile.js';
import { incrementDocCount } from '../utils/docCounter.js';
import { saveDocToHistory } from '../utils/docHistory.js';
import { callLLMWithMeta } from './llmService';
import { userKey } from '../utils/storageHelper.js';
import { supabase } from '../lib/supabaseClient.js';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { EUFlagIcon } from './EUFlagIcon.jsx';

// ── Item document token budget ──
// Each generated item covers a title, purpose/scope, detailed procedures, a
// mapping across all 3 frameworks, implementation steps, evidence, and a
// review/maintenance section — 2500 was tight enough to risk truncation on
// the more detailed topics (audit trail, incident response, etc.).
const ITEM_MAX_TOKENS = 3500;

function CrossFrameworkMapper({ onNavigate }) {
  const _profile = useSystemProfile(); // pre-fills organization context for AI prompts
  const [selectedTopic, setSelectedTopic] = useState('risk');
  const [generating, setGenerating] = useState(false);
  const [generatingItemIdx, setGeneratingItemIdx] = useState(null);
  const [generatedDoc, setGeneratedDoc] = useState('');
  const [activeDocKey, setActiveDocKey] = useState(null); // which item's doc is currently displayed
  const [showChecklist, setShowChecklist] = useState(true); // show by default so user sees items + buttons
  const [completedItems, setCompletedItems] = useState({});
  const [pdfGenerating, setPdfGenerating] = useState(false);


  // Per-(topic, item) stored docs:
  //   key   = `${topicId}_${itemIdx}`
  //   value = { content, framework, generated_at, expires_at }
  // active = expires_at > now (locks the per-item Generate button + shows the doc inline)
  // archived = expires_at <= now (shows old doc + allows regenerate)
  const [storedDocs, setStoredDocs] = useState({});


  // Risk result needed for primaryFramework (drives audit-cycle length + Q8 gate)
  const [riskResult, setRiskResult] = useState(null);
  useEffect(() => {
    try {
      const raw = localStorage.getItem(userKey('qais_risk_result'));
      setRiskResult(raw ? JSON.parse(raw) : null);
    } catch {}
  }, []);

  // ── Helpers: framework → audit cycle days ──
  const cycleDays = (framework) => {
    if (framework === 'nist') return 90;
    if (framework === 'eu')   return 365;
    if (framework === 'iso')  return 365;
    return 365; // default
  };
  const cycleLabel = (framework) => {
    if (framework === 'nist') return 'NIST AI RMF (90-day cycle)';
    if (framework === 'eu')   return 'EU AI Act (365-day cycle)';
    if (framework === 'iso')  return 'ISO/IEC 42001 (365-day cycle)';
    return '365-day cycle (default)';
  };
  const userFramework = riskResult?.primaryFramework || null;
  const hasRiskScore  = !!userFramework;

  // ── Load existing docs on mount: Supabase first, fall back to localStorage ──
  // Storage keys are per-(topic, itemIdx):
  //   localStorage: qais_mapper_<topic>_<itemIdx>
  //   Supabase doc_type: mapper_<topic>_<itemIdx>
  useEffect(() => {
    (async () => {

      const merged = {};

      // Local-first (instant)
      try {
        const allKeys = Object.keys(localStorage);
        for (const k of allKeys) {
          // Match userKey-prefixed key qais_mapper_<topic>_<idx>
          const m = k.match(/qais_mapper_([a-z]+)_(\d+)$/);
          if (m) {
            const raw = localStorage.getItem(k);
            if (raw) {
              try { merged[`${m[1]}_${m[2]}`] = JSON.parse(raw); } catch {}
            }
          }
        }
      } catch {}

      // Supabase override (most recent row per doc_type)
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          const { data, error } = await supabase
            .from('user_documents')
            .select('doc_type, content, framework, generated_at, expires_at')
            .eq('user_id', user.id)
            .like('doc_type', 'mapper_%')
            .order('generated_at', { ascending: false });
          if (!error && data) {
            const byType = {};
            data.forEach(d => { if (!byType[d.doc_type]) byType[d.doc_type] = d; });
            Object.entries(byType).forEach(([dt, row]) => {
              // doc_type format: mapper_<topic>_<itemIdx>
              const m = dt.match(/^mapper_([a-z]+)_(\d+)$/);
              if (!m) return; // skip legacy whole-topic rows
              const key = `${m[1]}_${m[2]}`;
              merged[key] = {
                content:      row.content,
                framework:    row.framework,
                generated_at: row.generated_at,
                expires_at:   row.expires_at,
              };
              try { localStorage.setItem(userKey(`qais_mapper_${key}`), JSON.stringify(merged[key])); } catch {}
            });
          }
        }
      } catch (e) {
        console.log('Supabase mapper fetch failed, using localStorage:', e.message);
      }

      setStoredDocs(merged);

    })();
  }, []);

  // ── Helpers ──
  const isActive = (rec) => {
    if (!rec || !rec.expires_at) return false;
    return new Date(rec.expires_at).getTime() > Date.now();
  };
  const itemKey = (topic, idx) => `${topic}_${idx}`;
  const getItemRecord = (topic, idx) => storedDocs[itemKey(topic, idx)];
  const isItemActive   = (topic, idx) => isActive(getItemRecord(topic, idx));
  const isItemArchived = (topic, idx) => {
    const r = getItemRecord(topic, idx);
    return r && !isActive(r);
  };

  // Active-doc info for the display panel (when a per-item doc has been selected to show)
  const activeRecord = activeDocKey ? storedDocs[activeDocKey] : null;
  const activeIsActive   = isActive(activeRecord);
  const activeIsArchived = activeRecord && !activeIsActive;

  // When topic changes, auto-show the most recently generated doc for that topic.
  // If none exists, clear the display.
  useEffect(() => {
    // Find all storedDocs keys belonging to this topic, pick the most recent.
    const topicKeys = Object.keys(storedDocs).filter(k => k.startsWith(selectedTopic + '_'));
    if (topicKeys.length === 0) {
      setActiveDocKey(null);
      setGeneratedDoc('');
      return;
    }
    // Sort by generated_at desc, pick newest
    const newest = topicKeys
      .map(k => ({ key: k, rec: storedDocs[k] }))
      .sort((a, b) => new Date(b.rec.generated_at).getTime() - new Date(a.rec.generated_at).getTime())[0];
    setActiveDocKey(newest.key);
    setGeneratedDoc(newest.rec.content);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTopic, storedDocs]);

  const mappings = {
    risk: {
      title: "Risk Management",
      description: "All frameworks require systematic risk assessment and management",
      iso: {
        reference: "Clauses 6.1 & 8.2",
        requirements: [
          "Actions to address risks and opportunities",
          "AI system impact assessment",
          "Risk identification and evaluation"
        ]
      },
      eu: {
        reference: "Article 9",
        requirements: [
          "Establish continuous risk management system",
          "Identify and analyze known and foreseeable risks",
          "Estimate and evaluate risks from intended use and misuse",
          "Adopt suitable risk management measures"
        ]
      },
      nist: {
        reference: "MAP 3, MEASURE 1, MANAGE 1",
        requirements: [
          "Identify potential costs and harms (MAP 3.2)",
          "Select and implement risk metrics (MEASURE 1.1)",
          "Prioritize risk treatment (MANAGE 1.2)"
        ]
      },
      unifiedApproach: "Conduct comprehensive risk assessment covering all three frameworks by: (1) Identifying risks to individuals, organization, and society (2) Evaluating likelihood and impact (3) Documenting mitigation measures (4) Tracking residual risks. A single risk register satisfies all three frameworks.",
      evidence: [
        "Risk register with all identified risks",
        "Risk assessment methodology document",
        "Impact analysis for each AI system",
        "Mitigation plans and implementation status",
        "Residual risk documentation with justification"
      ]
    },
    data: {
      title: "Data Governance",
      description: "Quality, representativeness, and bias in training data",
      iso: {
        reference: "Clause 8.3",
        requirements: [
          "Data management for AI systems throughout lifecycle",
          "Data quality requirements and metrics",
          "Data provenance and lineage tracking"
        ]
      },
      eu: {
        reference: "Article 10",
        requirements: [
          "Training data must be relevant, representative, and error-free",
          "Datasets examined for bias",
          "Data governance and management practices established"
        ]
      },
      nist: {
        reference: "MAP 2.1, MEASURE 2.1",
        requirements: [
          "Define data inputs and characteristics (MAP 2.1)",
          "Define validation datasets and processes (MEASURE 2.1)"
        ]
      },
      unifiedApproach: "Create comprehensive data governance framework documenting: data sources, quality metrics, representativeness analysis, bias testing results, and data lifecycle management. Single data governance document satisfies all frameworks.",
      evidence: [
        "Data governance policy",
        "Data quality metrics and measurements",
        "Dataset characteristics documentation",
        "Bias analysis report",
        "Data lineage and provenance tracking"
      ]
    },
    documentation: {
      title: "Documentation Requirements",
      description: "Technical and operational documentation across systems",
      iso: {
        reference: "Clauses 7.5 & 8.1",
        requirements: [
          "Documented information for AI management system",
          "Operational planning and control documentation",
          "Record retention and version control"
        ]
      },
      eu: {
        reference: "Article 11",
        requirements: [
          "Technical documentation before placing on market",
          "System description, development process, design choices",
          "Information on monitoring and control"
        ]
      },
      nist: {
        reference: "MAP 1.1, MAP 2.1, MEASURE 1.1",
        requirements: [
          "Document intended purposes (MAP 1.1)",
          "Document specific tasks and methods (MAP 2.1)",
          "Document risk metrics (MEASURE 1.1)"
        ]
      },
      unifiedApproach: "Create unified technical documentation package containing: system description, intended purpose, architecture, development methodology, data requirements, performance metrics, risk assessments, and monitoring procedures. Structured to satisfy all frameworks simultaneously.",
      evidence: [
        "System description and intended purpose",
        "Technical architecture documentation",
        "Development methodology and design decisions",
        "Testing and validation results",
        "Performance specifications and metrics"
      ]
    },
    testing: {
      title: "Testing & Validation",
      description: "Verification that AI systems perform as intended",
      iso: {
        reference: "Clauses 8.1 & 9.1",
        requirements: [
          "AI system verification and validation",
          "Monitoring, measurement, analysis and evaluation"
        ]
      },
      eu: {
        reference: "Article 15",
        requirements: [
          "Appropriate levels of accuracy, robustness, cybersecurity",
          "Performance consistency throughout lifecycle",
          "Resilience against errors and attempts to alter use"
        ]
      },
      nist: {
        reference: "MEASURE 2 & MEASURE 4",
        requirements: [
          "Validation and verification processes (MEASURE 2)",
          "Define test sets and validation processes (MEASURE 2.1)",
          "Demonstrate performance criteria (MEASURE 2.3)"
        ]
      },
      unifiedApproach: "Develop comprehensive testing protocol covering: functional testing, performance validation, fairness testing, robustness testing, and security testing. Document test methodology, results, and ongoing monitoring.",
      evidence: [
        "Test plan and methodology",
        "Test datasets description",
        "Test results for accuracy, fairness, robustness",
        "Performance benchmarks",
        "Validation against requirements"
      ]
    },
    oversight: {
      title: "Human Oversight",
      description: "Appropriate human involvement in AI decisions",
      iso: {
        reference: "Clauses 5.3 & 8.1",
        requirements: [
          "Organizational roles, responsibilities and authorities",
          "Operational planning and control (including oversight)"
        ]
      },
      eu: {
        reference: "Article 14",
        requirements: [
          "AI systems designed to allow effective oversight",
          "Understanding of capacities and limitations",
          "Ability to monitor operation and interpret outputs",
          "Ability to override or stop system"
        ]
      },
      nist: {
        reference: "GOVERN 2.2",
        requirements: [
          "Define roles, responsibilities, and authority",
          "Establish human-AI configuration throughout lifecycle"
        ]
      },
      unifiedApproach: "Define clear human oversight framework specifying: who has oversight responsibility, what authority they have (monitoring vs. override), when they intervene, what training they receive.",
      evidence: [
        "Human oversight procedures",
        "RACI matrix for roles and responsibilities",
        "Override and escalation procedures",
        "Training requirements for oversight personnel"
      ]
    },
    monitoring: {
      title: "Monitoring & Maintenance",
      description: "Ongoing performance tracking and system updates",
      iso: {
        reference: "Clauses 9.1 & 10.1",
        requirements: [
          "Monitoring, measurement, analysis and evaluation",
          "Continual improvement processes"
        ]
      },
      eu: {
        reference: "Article 72",
        requirements: [
          "Post-market monitoring system established",
          "Actively and systematically collect and analyze data",
          "Review data from system in use"
        ]
      },
      nist: {
        reference: "MEASURE 4, MANAGE 2.2",
        requirements: [
          "Ongoing monitoring program (MEASURE 4.1)",
          "Mechanisms to sustain deployed AI systems (MANAGE 2.2)"
        ]
      },
      unifiedApproach: "Establish unified monitoring program tracking: performance metrics, user feedback, incidents, drift detection, and fairness metrics. Create regular reporting cadence and update procedures.",
      evidence: [
        "Monitoring plan with metrics and thresholds",
        "Monitoring reports (ongoing)",
        "Incident logs",
        "System update records",
        "Performance trend analysis"
      ]
    },


    transparency: {
      title: "Transparency & Explainability",
      description: "AI systems must be understandable and their outputs interpretable to stakeholders and regulators",
      iso: {
        reference: "ISO/IEC 42001 Clause 6.3, Annex A.5.2",
        requirements: [
          "Document AI system purpose, capabilities, and limitations",
          "Provide explainability for automated decisions",
          "Maintain transparency records for all stakeholders"
        ]
      },
      eu: {
        reference: "EU AI Act Article 13",
        requirements: [
          "Users must be informed when interacting with an AI system",
          "Provide instructions for use that are clear and meaningful",
          "Disclose system capabilities, limitations, and intended purpose"
        ]
      },
      nist: {
        reference: "NIST AI RMF MANAGE 2, GOVERN 2.2",
        requirements: [
          "Establish transparency and explainability policies (GOVERN 2.2)",
          "Document limitations and assumptions (MANAGE 2.5)",
          "Provide understandable explanations for AI outputs"
        ]
      },
      unifiedApproach: "Build a transparency register that includes: (1) Model card with intended use, training data, and limitations (2) User-facing disclosure language for AI interactions (3) Technical explainability methods (e.g., SHAP, LIME) for high-risk decisions (4) Documentation of any black-box components and why they're acceptable.",
      evidence: [
        "Model cards or fact sheets for each AI system",
        "User disclosure notices and consent records",
        "Explainability artifacts (feature importance, counterfactuals)",
        "Transparency policy document",
        "Audit of unexplained system components"
      ]
    },
    bias: {
      title: "Bias & Fairness",
      description: "Prevent discriminatory outcomes and ensure equitable AI performance across all demographic groups",
      iso: {
        reference: "ISO/IEC 42001 Clause 6.1, Annex A.4.2",
        requirements: [
          "Identify potential bias sources in data and algorithms",
          "Implement fairness metrics and thresholds",
          "Monitor for discriminatory impacts"
        ]
      },
      eu: {
        reference: "EU AI Act Article 10 (Data governance), Recital 44a",
        requirements: [
          "Training data must be unbiased and representative",
          "Detect and mitigate possible bias in data sets",
          "Assess AI system for differential outcomes"
        ]
      },
      nist: {
        reference: "NIST AI RMF MEASURE 3.1, MAP 2.3",
        requirements: [
          "Define fairness criteria with stakeholders (MAP 2.3)",
          "Select and compute bias metrics (MEASURE 3.1)",
          "Document and address fairness trade-offs"
        ]
      },
      unifiedApproach: "Establish a fairness workflow: (1) Identify protected attributes relevant to your use case (2) Run pre‑, in‑, and post‑processing bias tests (3) Set fairness thresholds aligned with legal standards (e.g., demographic parity, equal opportunity) (4) Document any justified disparities and remediation steps.",
      evidence: [
        "Bias assessment plan",
        "Fairness metric results for each model version",
        "Data composition and representativeness report",
        "Mitigation actions (e.g., re‑sampling, re‑weighting)",
        "Annual fairness audit sign‑off"
      ]
    },
    security: {
      title: "Security & Privacy",
      description: "Protect AI systems against adversarial attacks, data breaches, and unauthorized access",
      iso: {
        reference: "ISO/IEC 42001 Annex A.6, ISO/IEC 27001 integration",
        requirements: [
          "Implement secure AI development lifecycle",
          "Perform threat modeling specific to AI",
          "Apply access control and encryption for models and data"
        ]
      },
      eu: {
        reference: "EU AI Act Article 15 (Accuracy, robustness, cybersecurity)",
        requirements: [
          "Resilience against manipulation and attacks",
          "Data protection by design and default (GDPR + AI Act)",
          "Security measures for training and inference environments"
        ]
      },
      nist: {
        reference: "NIST AI RMF MAP 5, MANAGE 4, NIST SP 800-53",
        requirements: [
          "Identify adversarial threat vectors (MAP 5.1)",
          "Apply AI-specific security controls (MANAGE 4.2)",
          "Conduct penetration testing on AI components"
        ]
      },
      unifiedApproach: "Adopt a secure AI pipeline: (1) Threat model using MITRE ATLAS framework (2) Validate input sanitization against adversarial examples (3) Encrypt models at rest and in transit (4) Maintain privacy via differential privacy or federated learning when required.",
      evidence: [
        "AI threat model and risk assessment",
        "Adversarial robustness test results",
        "Access logs for model and training data",
        "Encryption and key management policy",
        "Third‑party AI security audit"
      ]
    },
    incident: {
      title: "Incident Response",
      description: "Prepare for, detect, and remediate AI system failures, harmful outputs, or security breaches",
      iso: {
        reference: "ISO/IEC 42001 Clause 8.7, Annex A.8.5",
        requirements: [
          "Establish incident management process for AI systems",
          "Define severity levels and escalation paths",
          "Conduct post‑incident reviews and corrective actions"
        ]
      },
      eu: {
        reference: "EU AI Act Article 62 (Post‑market monitoring), Article 72",
        requirements: [
          "Report serious incidents to national authorities within 15 days",
          "Implement a post‑market monitoring plan",
          "Take corrective action and notify users"
        ]
      },
      nist: {
        reference: "NIST AI RMF MANAGE 3, MEASURE 2.4",
        requirements: [
          "Establish incident response playbooks (MANAGE 3.1)",
          "Monitor for emergent behavior (MEASURE 2.4)",
          "Perform root cause analysis on incidents"
        ]
      },
      unifiedApproach: "Create an AI incident response plan covering: (1) Detection (automated monitoring + human reports) (2) Triage (impact, scope, severity) (3) Containment (rollback, shadow mode) (4) Eradication (fix model/data) (5) Recovery (re‑deployment) (6) Lessons learned and disclosure as required by EU AI Act Article 72.",
      evidence: [
        "AI incident response plan document",
        "Incident log with timestamps and actions",
        "Post‑market monitoring report",
        "Example runbook for common failure modes",
        "Quarterly incident response drill results"
      ]
    },
    vendor: {
      title: "Vendor & 3rd‑Party Governance",
      description: "Manage risks when using external AI models, APIs, or data sources",
      iso: {
        reference: "ISO/IEC 42001 Clause 7.1, Annex A.7.2",
        requirements: [
          "Assess and monitor external AI providers",
          "Contractually require compliance with AI management system",
          "Maintain inventory of third‑party AI components"
        ]
      },
      eu: {
        reference: "EU AI Act Article 28 (Providers of general‑purpose AI), Article 29",
        requirements: [
          "Deployers must ensure providers meet compliance obligations",
          "Conduct due diligence on foundation models",
          "Maintain documentation from third‑party providers"
        ]
      },
      nist: {
        reference: "NIST AI RMF GOVERN 1.3, MAP 1.4",
        requirements: [
          "Map AI supply chain and dependencies (MAP 1.4)",
          "Set risk acceptance criteria for third‑party AI",
          "Monitor vendor compliance continuously"
        ]
      },
      unifiedApproach: "Implement a third‑party AI governance workflow: (1) Pre‑contract assessment (security, bias, transparency) (2) Contract clauses mandating compliance with AI Act / NIST / ISO (3) Ongoing monitoring via vendor attestations or technical audits (4) Exit strategy for model replacement if vendor fails compliance.",
      evidence: [
        "Vendor inventory with AI component mapping",
        "Due diligence questionnaire and results",
        "Contract compliance annexes",
        "Quarterly vendor risk re‑assessment",
        "Incidents from third‑party AI (if any)"
      ]
    },
    audit: {
      title: "Audit Trail & Logging",
      description: "Maintain verifiable records of AI decisions, data usage, and system changes for accountability",
      iso: {
        reference: "ISO/IEC 42001 Clause 9.2, Annex A.8.3",
        requirements: [
          "Log AI system inputs, outputs, and internal states",
          "Retain logs according to legal and business needs",
          "Implement tamper‑evident audit trails"
        ]
      },
      eu: {
        reference: "EU AI Act Article 18 (Record‑keeping), Article 61",
        requirements: [
          "Automatic recording of events for high‑risk AI",
          "Logs must include timestamps, inputs, outputs, and operator",
          "Provide logs to authorities upon request"
        ]
      },
      nist: {
        reference: "NIST AI RMF MEASURE 4.2, MANAGE 4.3",
        requirements: [
          "Establish auditable history of AI decisions (MEASURE 4.2)",
          "Implement logging for model updates and data provenance",
          "Secure logs from unauthorized modification"
        ]
      },
      unifiedApproach: "Design a centralized audit system that captures: (1) Every API call to an AI model (input, output, confidence) (2) Model version changes and A/B test results (3) Training data lineage and transformation steps (4) Human override or review actions (5) System access and configuration changes. Store in immutable storage (e.g., blockchain or WORM) with 5‑year retention for high‑risk AI.",
      evidence: [
        "Audit log schema and retention policy",
        "Sample logs from production AI system",
        "Log integrity protection mechanism (e.g., hashing, signing)",
        "Audit trail access control policy",
        "Annual log review and reconciliation report"
      ]
    }

  };

  const topics = [
    { id: 'risk', name: '⚠️ Risk Management', color: '#ef4444' },
    { id: 'data', name: '📊 Data Governance', color: '#8b5cf6' },
    { id: 'documentation', name: '📄 Documentation', color: '#3b82f6' },
    { id: 'testing', name: '🧪 Testing & Validation', color: '#10b981' },
    { id: 'oversight', name: '👤 Human Oversight', color: '#f59e0b' },
    { id: 'monitoring', name: '📈 Monitoring', color: '#06b6d4' },

    { id: 'audit', name: '📋 Audit Trail & Logging', color: '#a855f7' },
    { id: 'bias', name: '⚖️ Bias & Fairness', color: '#14b8a6' },
    { id: 'security', name: '🔐 Security & Privacy', color: '#6366f1' },
    { id: 'incident', name: '🚨 Incident Response', color: '#ef4444' },
    { id: 'vendor', name: '🤝 Vendor & 3rd-Party Governance', color: '#f97316' },
    { id: 'transparency', name: '🔍 Transparency & Explainability', color: '#ec489a' }

  ];

  const current = mappings[selectedTopic];
  const currentColor = topics.find(t => t.id === selectedTopic)?.color || '#3b82f6';

  // ── Convert a block of Markdown-table lines into an HTML <table> ──
  const renderMarkdownTable = (lines) => {
    // lines: array of raw '| a | b |' strings, including the '|---|---|' separator row
    const parseRow = (line) =>
      line
        .trim()
        .replace(/^\|/, '')
        .replace(/\|$/, '')
        .split('|')
        .map((cell) => cell.trim());

    const rows = lines
      .map(parseRow)
      // drop the '---' separator row
      .filter((cells) => !cells.every((c) => /^:?-{2,}:?$/.test(c)));

    if (rows.length === 0) return '';

    const [headerRow, ...bodyRows] = rows;
    const headerHtml = `<tr>${headerRow
      .map(
        (cell) =>
          `<th style="border: 1px solid #cbd5e1; background: #e2e8f0; color: #0e7c7c; padding: 6px 10px; text-align: left; font-size: 13px;">${cell}</th>`
      )
      .join('')}</tr>`;

    const bodyHtml = bodyRows
      .map(
        (cells) =>
          `<tr>${cells
            .map(
              (cell) =>
                `<td style="border: 1px solid #cbd5e1; padding: 6px 10px; font-size: 13px; vertical-align: top;">${cell}</td>`
            )
            .join('')}</tr>`
      )
      .join('');

    return `<table style="border-collapse: collapse; width: 100%; margin: 10px 0 16px 0;">${headerHtml}${bodyHtml}</table>`;
  };

  // Helper: clean markdown headings (remove # and * from heading lines)
  // ── Render markdown headings/bold as dark cyan underlined HTML (for screen + PDF) ──
  const renderAiMarkdown = (text) => {
    if (!text) return '';

    // ── Pull out Markdown table blocks first and replace with real <table> HTML,
    // so raw '|' syntax never reaches the plain-text formatting below. ──
    const lines = text.split('\n');
    const outLines = [];
    let i = 0;
    while (i < lines.length) {
      const line = lines[i];
      const isTableRow = /^\s*\|.*\|\s*$/.test(line);
      const nextIsSeparator = i + 1 < lines.length && /^\s*\|?[\s:|-]*-{2,}[\s:|-]*\|?\s*$/.test(lines[i + 1]);
      if (isTableRow && nextIsSeparator) {
        const tableLines = [];
        while (i < lines.length && /^\s*\|.*\|\s*$/.test(lines[i])) {
          tableLines.push(lines[i]);
          i++;
        }
        outLines.push(renderMarkdownTable(tableLines));
      } else {
        outLines.push(line);
        i++;
      }
    }
    const withTables = outLines.join('\n');

    return withTables
      // Setext-style underlines (===, ---, ___ as standalone lines) — remove
      .replace(/^[=\-_]{3,}\s*$/gm, '')
      // ### Heading 3
      .replace(/^#{3}\s+(.+)$/gm, '<div style="color: #0e7c7c; text-decoration: underline; font-weight: bold; font-size: 16px; margin: 14px 0 6px 0;">$1</div>')
      // ## Heading 2
      .replace(/^#{2}\s+(.+)$/gm, '<div style="color: #0e7c7c; text-decoration: underline; font-weight: bold; font-size: 18px; margin: 16px 0 8px 0;">$1</div>')
      // # Heading 1
      .replace(/^#{1}\s+(.+)$/gm, '<div style="color: #0e7c7c; text-decoration: underline; font-weight: bold; font-size: 20px; margin: 18px 0 10px 0;">$1</div>')
      // Bullet lines — the model inconsistently uses "-", "*", or a literal "•" as the
      // prefix. Normalize all three to the same "• " marker so bullets look uniform
      // regardless of which style this particular response happened to use.
      .replace(/^[•*-]\s+(.+)$/gm, '<div style="margin-left: 18px; margin-bottom: 4px;">• $1</div>')
      // Inline **bold** → dark cyan underlined
      .replace(/\*\*(.+?)\*\*/g, '<span style="color: #0e7c7c; text-decoration: underline; font-weight: bold;">$1</span>')
      // Inline *bold* (single asterisk) — the model sometimes emphasizes labels this way
      // instead of "**...**". Render it identically to double-asterisk bold rather than
      // leaving the literal "*" characters on the page. Runs after the double-asterisk
      // pass above, so by this point no "**" pairs remain to conflict with.
      .replace(/\*([^*\n]+?)\*/g, '<span style="color: #0e7c7c; text-decoration: underline; font-weight: bold;">$1</span>')
      // Inline _italic_ → italic
      .replace(/(?<![\w_])_([^_\n]+)_(?![\w_])/g, '<em>$1</em>')
      // Cleanup
      .replace(/<\/div>\n+/g, '</div>')
      .replace(/<\/table>\n+/g, '</table>')
      .replace(/\n{2,}/g, '\n')
      .replace(/\n/g, '<br>')
      // A <table> that got wrapped in <br> tags on either side reads oddly — trim them
      .replace(/<br>\s*(<table)/g, '$1')
      .replace(/(<\/table>)\s*<br>/g, '$1');
  };

  // ── Detect a response that was cut off mid-generation (hit max_tokens) ──
  // Same heuristic used in the document generator and risk calculator: a
  // properly finished document should end in sentence-ending punctuation, a
  // closing markdown element, or a bullet/list line — not mid-word or on a
  // dangling label/punctuation.
  const looksTruncated = (text) => {
    if (!text) return true;
    const trimmed = text.trim();
    const tail = trimmed.slice(-40);
    if (/[|\-–—:]\s*$/.test(trimmed)) return true;
    if (!/[.!?:)"'”\]]\s*$/.test(trimmed) && !/^[-*•]/.test(tail.split('\n').pop() || '')) {
      return true;
    }
    return false;
  };

  // Generate a focused compliance document for ONE checklist item (sub-topic)
  // within the currently selected topic. Each item is its own standalone document
  // with its own audit-cycle lock, stored as doc_type = mapper_<topic>_<itemIdx>.
  const generateUnifiedDocument = async (itemIdx) => {
    if (!hasRiskScore) {
      alert('Please complete the Risk Score assessment first. We use your primary framework to set the document audit cycle.');
      return;
    }
    if (typeof itemIdx !== 'number') {
      console.error('generateUnifiedDocument called without itemIdx');
      return;
    }
    if (isItemActive(selectedTopic, itemIdx)) {
      alert('This item is already generated for the current audit cycle. You can regenerate after the cycle expires.');
      return;
    }

    const currentTopic = mappings[selectedTopic];
    const itemText = currentTopic.evidence[itemIdx];
    if (!itemText) {
      console.error('Item not found at idx', itemIdx);
      return;
    }

    setGenerating(true);
    setGeneratingItemIdx(itemIdx);
    setActiveDocKey(itemKey(selectedTopic, itemIdx));
    setGeneratedDoc('');

    const profileContext = _profile.promptContext ? `\n\nOrganization Context:\n${_profile.promptContext}` : '';
    const prompt = `Create a focused, standalone compliance document titled "${itemText}" for the topic of ${currentTopic.title}. This document is one of several deliverables under ${currentTopic.title}; it should be self-contained and ready to file with auditors.${profileContext}

The document must satisfy requirements from ALL THREE frameworks for this specific deliverable:

ISO/IEC 42001 Requirements (${currentTopic.iso.reference}):
${currentTopic.iso.requirements.map((r, i) => `${i + 1}. ${r}`).join('\n')}

EU AI Act Requirements (${currentTopic.eu.reference}):
${currentTopic.eu.requirements.map((r, i) => `${i + 1}. ${r}`).join('\n')}

NIST AI RMF Requirements (${currentTopic.nist.reference}):
${currentTopic.nist.requirements.map((r, i) => `${i + 1}. ${r}`).join('\n')}

Unified Approach context:
${currentTopic.unifiedApproach}

Create a professional document FOCUSED ON "${itemText}". Include:
1. Title (matching "${itemText}")
2. Purpose and Scope (specific to this deliverable)
3. Detailed content/procedures for "${itemText}"
4. How this deliverable maps to ISO 42001, EU AI Act, and NIST AI RMF requirements above
5. Implementation Steps
6. Evidence of Completion (what proves this deliverable is in place)
7. Review and Maintenance

Format with clear sections and bullet points. Make it actionable, complete, and ready for audit. Do NOT cover other items under ${currentTopic.title} — only "${itemText}".

Formatting must be consistent throughout the entire document: always use "- " (hyphen plus space) for bullet points — never "•" or "*" as a bullet marker. Always use "**Label:**" (double asterisk) to bold a label — never a single asterisk like "*Label:*". Do not mix styles within the same document.`;

    try {
      const first = await callLLMWithMeta({
        messages: [
          {
            role: 'system',
            content: 'You are an expert at creating focused, standalone compliance documents that satisfy multiple regulatory frameworks simultaneously. Each document you produce is a single, self-contained deliverable ready to be filed with auditors. Formatting must be consistent throughout: always use "- " for bullets and "**Label:**" (double asterisk) for bolded labels — never "•", a single asterisk, or a mix of styles.'
          },
          { role: 'user', content: prompt }
        ],
        temperature: 0.7,
        max_tokens: ITEM_MAX_TOKENS
      });

      let content = first.content;
      const firstProvider = first.provider; // which provider answered — used to prefer it on a continuation

      // ── If the response looks cut off, ask the model to finish it rather than
      // silently handing the user a broken document. One continuation attempt,
      // preferring the same provider that wrote the first half so voice and
      // formatting stay consistent (falls back to the rest of the chain if that
      // provider is no longer available). ──
      if (looksTruncated(content)) {
        try {
          const continuation = await callLLMWithMeta({
            messages: [
              {
                role: 'system',
                content: 'You are an expert at creating focused, standalone compliance documents. Formatting must stay consistent with the text already generated below: match its existing bullet marker ("- ") and bold style ("**Label:**") exactly rather than switching conventions partway through.'
              },
              { role: 'user', content: prompt },
              { role: 'assistant', content },
              { role: 'user', content: 'Your previous response was cut off before it finished. Continue exactly where you left off — do not repeat any earlier content, and do not restart the document.' },
            ],
            temperature: 0.7,
            max_tokens: ITEM_MAX_TOKENS,
            preferredProvider: firstProvider,
          });
          content = content + continuation.content;
        } catch (contErr) {
          console.warn('Continuation attempt failed, keeping original (possibly truncated) content:', contErr);
        }
      }

      // ── Persist: localStorage + Supabase ──
      const now = new Date();
      const expires = new Date(now.getTime() + cycleDays(userFramework) * 86400 * 1000);
      const record = {
        content,
        framework:    userFramework,
        generated_at: now.toISOString(),
        expires_at:   expires.toISOString(),
        item_title:   itemText, // for download filename + display
        item_idx:     itemIdx,
      };

      const key = itemKey(selectedTopic, itemIdx);
      try { localStorage.setItem(userKey(`qais_mapper_${key}`), JSON.stringify(record)); } catch {}

      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          await supabase.from('user_documents').insert({
            user_id:      user.id,
            doc_type:     `mapper_${key}`,
            content,
            framework:    userFramework,
            generated_at: record.generated_at,
            expires_at:   record.expires_at,
          });
        }
      } catch (e) {
        console.log('Supabase mapper save failed; localStorage retains it:', e.message);
      }

      setStoredDocs(prev => ({ ...prev, [key]: record }));
      setGeneratedDoc(content);

      incrementDocCount();
      saveDocToHistory({
        tool: 'mapper',
        title: `Cross Framework: ${currentTopic.title} — ${itemText}`,
        preview: content.slice(0, 120),
        framework: 'All Frameworks',
      });
      window.dispatchEvent(new CustomEvent('qais-journey-update'));

    } catch (error) {
      console.error('All providers failed:', error);
      setGeneratedDoc('❌ All AI providers are currently unavailable. Please try again in a moment.');
    }

    setGenerating(false);
    setGeneratingItemIdx(null);
  };

  // ── Load real logo from /public ──
  const loadImageAsBase64 = async (path) => {
    try {
      const response = await fetch(path);
      const blob = await response.blob();
      return await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch (e) {
      console.warn('Logo load failed:', e);
      return null;
    }
  };

  // ── Styled PDF download (logo cover + per-page header/footer + smart slicing) ──
  const downloadDocument = async () => {
    if (!generatedDoc) return;
    setPdfGenerating(true);
    await new Promise(resolve => setTimeout(resolve, 50));

    const logoBase64   = await loadImageAsBase64('/logo.png');
    const topicMeta    = topics.find(t => t.id === selectedTopic) || { name: current.title, color: '#3b82f6' };
    // Strip leading emoji + space from topic name for clean text
    const topicLabel   = topicMeta.name.replace(/^[^\s]+\s/, '');
    const topicColor   = topicMeta.color;
    const companyName  = _profile.companyName || '';
    const itemTitle    = activeRecord?.item_title || '';
    const coverTitle   = `${itemTitle || topicLabel}${companyName ? ` — ${companyName}` : ''}`;
    const headerTitle  = `Quantum AI Shield · ${topicLabel}${itemTitle ? ` · ${itemTitle}` : ''}`;
    const cycle        = cycleLabel(userFramework);
    const generatedAt  = activeRecord?.generated_at ? new Date(activeRecord.generated_at) : new Date();
    const nextDate     = activeRecord?.expires_at   ? new Date(activeRecord.expires_at)   : null;

    const htmlContent = `
      <div style="font-family: 'Inter', 'Helvetica', sans-serif; max-width: 800px; margin: 0 auto; padding: 20px; background: white; color: #1e293b; line-height: 1.6; font-size: 15px;">
        ${logoBase64 ? `<div style="text-align: center; margin-bottom: 8px;"><img src="${logoBase64}" style="max-width: 180px; max-height: 90px;" alt="QuantumAI Shield" /></div>` : ''}
        <h1 style="color: #0e7c7c; border-bottom: 3px solid #0e7c7c; padding-bottom: 10px; font-size: 28px; text-align: center;">${coverTitle}</h1>
        <p style="text-align: center;"><strong>Cross-Framework Unified Document by QuantumAI Shield</strong><br>${generatedAt.toLocaleDateString()}</p>

        <!-- Topic + framework band -->
        <div style="background: ${topicColor}1a; border: 3px solid ${topicColor}; border-radius: 12px; padding: 18px; margin: 20px 0; text-align: center;">
          <div style="font-size: 12px; color: ${topicColor}; font-weight: 700; letter-spacing: 0.05em; text-transform: uppercase; margin-bottom: 6px;">Compliance Topic</div>
          <div style="font-size: 22px; color: ${topicColor}; font-weight: 800; margin-bottom: 6px;">${topicLabel.toUpperCase()}</div>
          <div style="font-size: 13px; color: #475569;">
            <strong>Frameworks:</strong> ISO 42001 + EU AI Act + NIST AI RMF
            &nbsp;|&nbsp; <strong>Cycle:</strong> ${cycle}
          </div>
          <div style="font-size: 13px; color: #475569; margin-top: 4px;">
            <strong>Generated:</strong> ${generatedAt.toLocaleDateString()}
            ${nextDate ? `&nbsp;|&nbsp; <strong>Next regeneration:</strong> ${nextDate.toLocaleDateString()}` : ''}
          </div>
        </div>

        <h2 style="color: #0e7c7c; border-bottom: 2px solid #0e7c7c; font-size: 22px;">Framework References</h2>
        <p>
          <strong>ISO/IEC 42001:</strong> ${current.iso.reference}<br>
          <strong>EU AI Act:</strong> ${current.eu.reference}<br>
          <strong>NIST AI RMF:</strong> ${current.nist.reference}
        </p>

        <h2 style="color: #0e7c7c; border-bottom: 2px solid #0e7c7c; font-size: 22px;">${topicLabel}</h2>
        <div style="background: #f8fafc; padding: 16px 20px; border-radius: 10px; font-size: 15px; line-height: 1.7; word-wrap: break-word; border-left: 4px solid ${topicColor};">
          ${renderAiMarkdown(generatedDoc)}
        </div>

        <h2 style="color: #0e7c7c; border-bottom: 2px solid #0e7c7c; font-size: 22px;">✏️ Implementation Checklist</h2>
        ${itemTitle ? `<p style="margin: 4px 0 12px; color: #64748b; font-size: 13px;">⭐ This document covers the highlighted item below. Other items are separate deliverables.</p>` : ''}
        <div style="background: white; border: 2px solid ${topicColor}; border-radius: 10px; padding: 16px 20px; page-break-inside: avoid;">
          ${current.evidence.map((item, idx) => {
            const key = `${selectedTopic}_${idx}`;
            const isCompleted = !!completedItems[key];
            const isThisDoc = activeRecord?.item_idx === idx;
            return `
              <div style="display: flex; align-items: flex-start; gap: 10px; padding: 8px 10px; margin: 4px 0; border-radius: 6px; ${isThisDoc ? 'background: #dbeafe; border: 2px solid #3b82f6;' : 'border-bottom: 1px solid #f1f5f9;'}">
                <div style="width: 18px; height: 18px; border-radius: 4px; border: 2px solid ${isCompleted ? '#22c55e' : '#cbd5e1'}; background: ${isCompleted ? '#22c55e' : 'white'}; flex-shrink: 0; display: flex; align-items: center; justify-content: center; margin-top: 2px;">
                  ${isCompleted ? '<span style="color: white; font-size: 12px; font-weight: bold;">✓</span>' : ''}
                </div>
                <span style="color: ${isThisDoc ? '#1e3a8a' : (isCompleted ? '#15803d' : '#334155')}; font-weight: ${isThisDoc ? '700' : '400'}; text-decoration: ${isCompleted ? 'line-through' : 'none'}; font-size: 14px; line-height: 1.5;">
                  ${isThisDoc ? '⭐ ' : ''}${item}${isThisDoc ? ' <span style="color: #3b82f6; font-size: 12px; font-weight: 700;">(this document)</span>' : ''}
                </span>
              </div>
            `;
          }).join('')}
          <div style="margin-top: 12px; padding: 10px 14px; background: #fef3c7; border-radius: 6px;">
            <strong style="color: #92400e; font-size: 13px;">Progress:</strong>
            <span style="color: #92400e; font-size: 13px;"> ${Object.keys(completedItems).filter(k => k.startsWith(selectedTopic + '_') && completedItems[k]).length} of ${current.evidence.length} items completed</span>
          </div>
        </div>

        <h2 style="color: #854d0e; border-bottom: 2px solid #eab308; font-size: 22px;">📋 Evidence / Documentation Needed</h2>
        <div style="background: #fef3c7; border: 2px solid #eab308; border-radius: 10px; padding: 14px 20px; page-break-inside: avoid;">
          <ul style="margin: 0; padding-left: 22px; color: #92400e;">
            ${current.evidence.map(item => `<li style="margin-bottom: 6px; font-size: 14px; line-height: 1.6;">${item}</li>`).join('')}
          </ul>
        </div>

        <h2 style="color: #0e7c7c; border-bottom: 2px solid #0e7c7c; font-size: 22px;">💡 Efficiency Tips</h2>
        <div style="background: white; border: 2px solid #e2e8f0; border-radius: 10px; padding: 14px 20px; page-break-inside: avoid;">
          ${[
            { tip: 'Use a single risk register', detail: 'for all frameworks — just map risks to specific requirements in each framework' },
            { tip: 'Create modular documentation', detail: 'that can be assembled for different audiences (auditors, regulators, internal teams)' },
            { tip: 'Implement a unified monitoring dashboard', detail: 'tracking metrics for all frameworks simultaneously' },
            { tip: 'Conduct a single impact assessment', detail: 'covering ISO, EU, and NIST requirements instead of three separate assessments' },
            { tip: 'Use cross-functional teams', detail: 'to avoid siloed compliance efforts — one team, one plan, three frameworks satisfied' },
          ].map(({ tip, detail }) => `
            <div style="display: flex; gap: 10px; align-items: flex-start; padding: 8px 0; border-bottom: 1px solid #f1f5f9;">
              <span style="font-size: 16px; flex-shrink: 0;">✅</span>
              <span style="color: #334155; font-size: 14px; line-height: 1.6;"><strong>${tip}</strong> ${detail}</span>
            </div>
          `).join('')}
        </div>

        <h2 style="color: #0e7c7c; border-bottom: 2px solid #0e7c7c; font-size: 22px;">Disclaimer</h2>
        <p style="font-size: 12px; color: #64748b;">AI-generated compliance guidance — not legal advice. Always verify with a qualified compliance professional before use.</p>
      </div>
    `;

    const container = document.createElement('div');
    container.style.position = 'absolute';
    container.style.left = '-9999px';
    container.style.top = '0';
    container.style.width = '800px';
    container.style.backgroundColor = 'white';
    container.style.zIndex = '-1';
    container.innerHTML = htmlContent;
    document.body.appendChild(container);

    try {
      await new Promise(resolve => setTimeout(resolve, 300));
      const canvas = await html2canvas(container, { scale: 2, backgroundColor: '#ffffff', logging: false, useCORS: false });

      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth  = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();

      const HEADER_HEIGHT = 28;
      const FOOTER_HEIGHT = 22;
      const SIDE_MARGIN   = 14; // mm — page-level left/right margin (was 0, edge-to-edge)

      const usableWidth  = pdfWidth - 2 * SIDE_MARGIN;
      const usableHeight = pdfHeight - HEADER_HEIGHT - FOOTER_HEIGHT;
      const imgWidth     = usableWidth;
      const imgHeight    = (canvas.height * imgWidth) / canvas.width;

      // ── Smart slicing: detect blank rows for clean page breaks ──
      const canvasCtx = canvas.getContext('2d');
      const imageData = canvasCtx.getImageData(0, 0, canvas.width, canvas.height);
      const data = imageData.data;
      const cw = canvas.width;

      const isRowBlank = (y) => {
        let darkPixels = 0;
        for (let x = 0; x < cw; x += 4) {
          const i = (y * cw + x) * 4;
          if (data[i] < 100 || data[i + 1] < 100 || data[i + 2] < 100) {
            darkPixels++;
            if (darkPixels > 2) return false;
          }
        }
        return true;
      };

      const pxPerMm = canvas.height / imgHeight;
      const usableHeightPx = Math.floor(usableHeight * pxPerMm);

      const blankRows = [];
      for (let y = 0; y < canvas.height; y += 2) {
        if (isRowBlank(y)) blankRows.push(y);
      }

      const pageBreaks = [0];
      let currentY = 0;
      while (currentY < canvas.height) {
        const target = currentY + usableHeightPx;
        if (target >= canvas.height) break;
        let breakAt = target;
        const minAllowed = currentY + usableHeightPx * 0.35;
        for (let i = blankRows.length - 1; i >= 0; i--) {
          if (blankRows[i] <= target && blankRows[i] > minAllowed) {
            breakAt = blankRows[i];
            break;
          }
        }
        pageBreaks.push(breakAt);
        currentY = breakAt;
      }

      // Drop trailing blank pages
      let lastContentRow = 0;
      for (let y = canvas.height - 1; y >= 0; y -= 2) {
        if (!isRowBlank(y)) { lastContentRow = y; break; }
      }
      while (pageBreaks.length > 1 && pageBreaks[pageBreaks.length - 1] >= lastContentRow - 20) {
        pageBreaks.pop();
      }
      const totalPages = pageBreaks.length;

      const drawHeaderFooter = (pageNum) => {
        if (pageNum > 1) {
          pdf.setFontSize(11);
          pdf.setTextColor(80, 80, 80);
          const logoW  = logoBase64 ? 10 : 0;
          const gap    = logoBase64 ? 3  : 0;
          const titleW = pdf.getTextWidth(headerTitle);
          const groupW = logoW + gap + titleW;
          const groupStartX = (pdfWidth - groupW) / 2;
          if (logoBase64) { try { pdf.addImage(logoBase64, 'PNG', groupStartX, 6, 10, 10); } catch {} }
          pdf.text(headerTitle, groupStartX + logoW + gap, 13);
          pdf.setDrawColor(200, 200, 200);
          pdf.setLineWidth(0.2);
          pdf.line(SIDE_MARGIN + 4, HEADER_HEIGHT - 4, pdfWidth - SIDE_MARGIN - 4, HEADER_HEIGHT - 4);
        }
        pdf.setFontSize(9);
        pdf.setTextColor(120, 120, 120);
        pdf.line(SIDE_MARGIN + 4, pdfHeight - FOOTER_HEIGHT + 6, pdfWidth - SIDE_MARGIN - 4, pdfHeight - FOOTER_HEIGHT + 6);
        pdf.text('www.QuantumAIShield.com',          SIDE_MARGIN + 4,            pdfHeight - 8);
        pdf.text(`Page ${pageNum} of ${totalPages}`, pdfWidth / 2,               pdfHeight - 8, { align: 'center' });
        pdf.text(new Date().toLocaleDateString(),    pdfWidth - SIDE_MARGIN - 4, pdfHeight - 8, { align: 'right' });
      };

      for (let pageNum = 1; pageNum <= totalPages; pageNum++) {
        if (pageNum > 1) pdf.addPage();
        const startPx = pageBreaks[pageNum - 1];
        const endPx   = pageNum < totalPages ? pageBreaks[pageNum] : canvas.height;
        const slicePx = endPx - startPx;
        const sliceMm = slicePx / pxPerMm;

        const tempCanvas = document.createElement('canvas');
        tempCanvas.width  = canvas.width;
        tempCanvas.height = slicePx;
        const tempCtx = tempCanvas.getContext('2d');
        tempCtx.fillStyle = '#ffffff';
        tempCtx.fillRect(0, 0, canvas.width, slicePx);
        tempCtx.drawImage(canvas, 0, startPx, canvas.width, slicePx, 0, 0, canvas.width, slicePx);
        const sliceData = tempCanvas.toDataURL('image/png');

        pdf.addImage(sliceData, 'PNG', SIDE_MARGIN, HEADER_HEIGHT, imgWidth, sliceMm);
        pdf.setFillColor(255, 255, 255);
        pdf.rect(0, 0, pdfWidth, HEADER_HEIGHT, 'F');
        pdf.rect(0, pdfHeight - FOOTER_HEIGHT, pdfWidth, FOOTER_HEIGHT, 'F');
        drawHeaderFooter(pageNum);
      }

      const safeItem = (activeRecord?.item_title || '').replace(/[^a-zA-Z0-9]+/g, '_').slice(0, 40);
      const filenameSuffix = safeItem ? `_${safeItem}` : '';
      pdf.save(`QuantumAIShield_${selectedTopic}${filenameSuffix}_${companyName || 'AI'}_${Date.now()}.pdf`);
    } catch (err) {
      console.error('PDF generation error:', err);
      alert('Failed to generate PDF. Please try again.');
    } finally {
      document.body.removeChild(container);
      setPdfGenerating(false);
    }
  };

  const toggleChecklistItem = (itemIndex) => {
    const key = `${selectedTopic}_${itemIndex}`;
    setCompletedItems(prev => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <div style={{ padding: '40px', maxWidth: '1400px', margin: '0 auto', backgroundColor: '#f8fafc', minHeight: '100vh' }}>

      {/* Header */}
      <div style={{ marginBottom: '40px' }}>
        <h1 style={{ fontSize: '42px', fontWeight: 'bold', marginBottom: '10px', color: '#0f172a' }}>
          🔗 Cross-Framework Mapper
        </h1>
        <p style={{ fontSize: '22px', color: '#64748b', marginBottom: '20px' }}>
          See how ONE action satisfies requirements across ALL THREE frameworks
        </p>
        <div style={{ backgroundColor: '#dbeafe', border: '2px solid #3b82f6', borderRadius: '12px', padding: '20px', marginTop: '20px' }}>
          <p style={{ color: '#1e40af', fontSize: '15px', margin: 0 }}>
            <strong>💡 Your Competitive Advantage: Instead of doing separate work for ISO 42001, EU AI Act, and NIST AI RMF, map your compliance activities once and satisfy all three simultaneously!</strong>
          </p>
        </div>
      </div>

      {/* Topic Selector */}
      <div style={{ marginBottom: '40px' }}>
        <h2 style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '20px', color: '#0f172a' }}>
          Select a Compliance Topic:
        </h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '15px' }}>
          {topics.map(topic => (
            <button
              key={topic.id}
              onClick={() => setSelectedTopic(topic.id)}
              style={{
                padding: '20px',
                backgroundColor: selectedTopic === topic.id ? topic.color : 'white',
                color: selectedTopic === topic.id ? 'white' : '#0f172a',
                border: `3px solid ${topic.color}`,
                borderRadius: '12px',
                fontSize: '16px',
                fontWeight: 'bold',
                cursor: 'pointer',
                transition: 'all 0.3s',
                textAlign: 'left'
              }}
            >
              {topic.name}
            </button>
          ))}
        </div>
      </div>

      {/* Main Content */}
      <div style={{ marginBottom: '40px' }}>
        <div style={{ backgroundColor: 'white', padding: '30px', borderRadius: '20px', boxShadow: '0 4px 6px rgba(0,0,0,0.07)', borderLeft: `6px solid ${currentColor}` }}>
          <h2 style={{ fontSize: '32px', fontWeight: 'bold', marginBottom: '15px', color: '#0f172a' }}>
            {current.title}
          </h2>
          <p style={{ fontSize: '18px', color: '#64748b', marginBottom: '30px' }}>
            {current.description}
          </p>

          {/* Three Framework Cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px', marginBottom: '30px' }}>
            <div style={{ backgroundColor: '#eff6ff', padding: '25px', borderRadius: '15px', border: '2px solid #3b82f6' }}>
              <h3 style={{ fontSize: '20px', fontWeight: 'bold', marginBottom: '10px', color: '#1e40af' }}>📘 ISO/IEC 42001</h3>
              <p style={{ fontSize: '14px', color: '#1e3a8a', marginBottom: '15px', fontWeight: '600' }}>{current.iso.reference}</p>
              <ul style={{ margin: 0, paddingLeft: '20px', color: '#1e40af' }}>
                {current.iso.requirements.map((req, idx) => <li key={idx} style={{ marginBottom: '8px', lineHeight: '1.5' }}>{req}</li>)}
              </ul>
            </div>
            <div style={{ backgroundColor: '#faf5ff', padding: '25px', borderRadius: '15px', border: '2px solid #8b5cf6' }}>
            <h3 style={{ fontSize: '20px', fontWeight: 'bold', marginBottom: '10px', color: '#6b21a8', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <EUFlagIcon size={32} blue="#4285F4" glow={false} />
                EU AI Act
            </h3>
              <p style={{ fontSize: '14px', color: '#7e22ce', marginBottom: '15px', fontWeight: '600' }}>{current.eu.reference}</p>
              <ul style={{ margin: 0, paddingLeft: '20px', color: '#6b21a8' }}>
                {current.eu.requirements.map((req, idx) => <li key={idx} style={{ marginBottom: '8px', lineHeight: '1.5' }}>{req}</li>)}
              </ul>
            </div>
            <div style={{ backgroundColor: '#ecfeff', padding: '25px', borderRadius: '15px', border: '2px solid #06b6d4' }}>
              <h3 style={{ fontSize: '20px', fontWeight: 'bold', marginBottom: '10px', color: '#0e7490' }}>🏛️ NIST AI RMF</h3>
              <p style={{ fontSize: '14px', color: '#155e75', marginBottom: '15px', fontWeight: '600' }}>{current.nist.reference}</p>
              <ul style={{ margin: 0, paddingLeft: '20px', color: '#0e7490' }}>
                {current.nist.requirements.map((req, idx) => <li key={idx} style={{ marginBottom: '8px', lineHeight: '1.5' }}>{req}</li>)}
              </ul>
            </div>
          </div>

          {/* Unified Approach */}
          <div style={{ backgroundColor: '#dcfce7', padding: '25px', borderRadius: '15px', border: '3px solid #22c55e', marginBottom: '25px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '15px' }}>
              <h3 style={{ fontSize: '22px', fontWeight: 'bold', color: '#15803d', display: 'flex', alignItems: 'center', gap: '10px', margin: 0 }}>
                <span>✨</span><span>Unified Approach (Satisfies All Three!)</span>
              </h3>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  onClick={() => setShowChecklist(!showChecklist)}
                  style={{ padding: '10px 20px', backgroundColor: showChecklist ? '#15803d' : 'white', color: showChecklist ? 'white' : '#15803d', border: '2px solid #15803d', borderRadius: '8px', fontSize: '14px', fontWeight: 'bold', cursor: 'pointer', whiteSpace: 'nowrap' }}
                >
                  {showChecklist ? '✏️ Hide Checklist' : '📋 Show Checklist'}
                </button>
              </div>
            </div>
            <p style={{ fontSize: '16px', color: '#166534', lineHeight: '1.8', margin: 0 }}>{current.unifiedApproach}</p>

            {!hasRiskScore && (
              <div style={{ marginTop: '15px', padding: '12px 15px', backgroundColor: '#fef3c7', border: '2px solid #f59e0b', borderRadius: '8px' }}>
                <p style={{ margin: 0, color: '#854d0e', fontSize: '14px' }}>
                  🔒 <strong>Complete the Risk Score assessment first.</strong> We use your primary framework to set the audit-cycle length for generated documents.
                </p>
              </div>
            )}

            {/* Checklist with per-item Generate buttons */}
            {showChecklist && (
              <div style={{ marginTop: '20px', backgroundColor: 'white', padding: '20px', borderRadius: '10px', border: '2px solid #15803d' }}>
                <h4 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '6px', color: '#15803d' }}>✏️ Implementation Checklist</h4>
                <p style={{ fontSize: '13px', color: '#64748b', margin: '0 0 15px' }}>
                  Each item below is a separate standalone document. Click <strong>📄 Generate</strong> next to any item to create that specific deliverable.
                </p>
                {current.evidence.map((item, idx) => {
                  const key = `${selectedTopic}_${idx}`;
                  const isCompleted = completedItems[key] || false;
                  const rec        = getItemRecord(selectedTopic, idx);
                  const recActive  = isItemActive(selectedTopic, idx);
                  const recArchived = isItemArchived(selectedTopic, idx);
                  const isLoadingThis = generating && generatingItemIdx === idx;
                  const isViewing   = activeDocKey === itemKey(selectedTopic, idx);

                  return (
                    <div key={idx} style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      padding: '12px',
                      marginBottom: '8px',
                      backgroundColor: isViewing ? '#dbeafe' : (isCompleted ? '#dcfce7' : '#f8fafc'),
                      borderRadius: '8px',
                      border: '2px solid',
                      borderColor: isViewing ? '#3b82f6' : (isCompleted ? '#22c55e' : '#e2e8f0'),
                      transition: 'all 0.2s'
                    }}>
                      {/* Checkbox (toggles completion only) */}
                      <div
                        onClick={() => toggleChecklistItem(idx)}
                        style={{ width: '24px', height: '24px', borderRadius: '6px', border: '2px solid', borderColor: isCompleted ? '#22c55e' : '#cbd5e1', backgroundColor: isCompleted ? '#22c55e' : 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, cursor: 'pointer' }}
                      >
                        {isCompleted && <span style={{ color: 'white', fontSize: '16px' }}>✓</span>}
                      </div>
                      {/* Item text — click to view this item's generated doc (if any) */}
                      <span
                        onClick={() => {
                          if (rec) {
                            setActiveDocKey(itemKey(selectedTopic, idx));
                            setGeneratedDoc(rec.content);
                          }
                        }}
                        title={rec ? 'Click to view this document' : 'Generate the document first using the button on the right'}
                        style={{
                          flex: 1,
                          color: isCompleted ? '#15803d' : '#334155',
                          textDecoration: isCompleted ? 'line-through' : 'none',
                          fontSize: '15px',
                          lineHeight: '1.5',
                          cursor: rec ? 'pointer' : 'default',
                        }}
                      >
                        {item}
                      </span>
                      {/* Per-item Generate / View / Locked button */}
                      {recActive ? (
                        <button
                          onClick={() => {
                            setActiveDocKey(itemKey(selectedTopic, idx));
                            setGeneratedDoc(rec.content);
                          }}
                          title={`Already generated for this audit cycle (expires ${new Date(rec.expires_at).toLocaleDateString()})`}
                          style={{
                            padding: '8px 14px',
                            backgroundColor: isViewing ? '#1e40af' : '#94a3b8',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            fontSize: '12px',
                            fontWeight: '700',
                            cursor: 'pointer',
                            whiteSpace: 'nowrap',
                            flexShrink: 0,
                          }}
                        >
                          {isViewing ? '👁 Viewing' : '✓ Already generated'}
                        </button>
                      ) : (
                        <button
                          onClick={() => generateUnifiedDocument(idx)}
                          disabled={!hasRiskScore || generating}
                          title={
                            !hasRiskScore
                              ? 'Complete Risk Score first'
                              : recArchived
                                ? `Last generated ${new Date(rec.generated_at).toLocaleDateString()} — click to regenerate`
                                : 'Generate compliance document for this item'
                          }
                          style={{
                            padding: '8px 14px',
                            backgroundColor: (!hasRiskScore || generating) ? '#cbd5e1' : (recArchived ? '#f59e0b' : '#15803d'),
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            fontSize: '12px',
                            fontWeight: '700',
                            cursor: (!hasRiskScore || generating) ? 'not-allowed' : 'pointer',
                            whiteSpace: 'nowrap',
                            flexShrink: 0,
                          }}
                        >
                          {isLoadingThis
                            ? '⏳ Generating...'
                            : !hasRiskScore
                              ? '🔒 Score first'
                              : recArchived
                                ? '🔄 Regenerate'
                                : '📄 Generate'}
                        </button>
                      )}
                    </div>
                  );
                })}
                <div style={{ marginTop: '15px', padding: '12px', backgroundColor: '#fef3c7', borderRadius: '8px' }}>
                  <p style={{ margin: 0, color: '#92400e', fontSize: '14px' }}>
                    <strong>Progress:</strong> {Object.keys(completedItems).filter(k => k.startsWith(selectedTopic + '_') && completedItems[k]).length} of {current.evidence.length} items checked
                    {' · '}
                    <strong>Generated:</strong> {current.evidence.filter((_, i) => isItemActive(selectedTopic, i)).length} of {current.evidence.length} documents
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Generated Document Display */}
          {generatedDoc && (
            <div style={{ backgroundColor: 'white', padding: '25px', borderRadius: '15px', border: '3px solid #22c55e', marginBottom: '25px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h3 style={{ fontSize: '22px', fontWeight: 'bold', color: '#15803d', margin: 0 }}>
                  📄 {activeRecord?.item_title ? activeRecord.item_title : 'Generated Unified Document'}
                </h3>
                <button
                  onClick={downloadDocument}
                  disabled={pdfGenerating}
                  style={{
                    padding: '12px 24px',
                    backgroundColor: pdfGenerating ? '#cbd5e1' : '#15803d',
                    color: 'white', border: 'none', borderRadius: '8px',
                    fontSize: '16px', fontWeight: 'bold',
                    cursor: pdfGenerating ? 'wait' : 'pointer',
                    display: 'flex', alignItems: 'center', gap: '8px'
                  }}
                >
                  {pdfGenerating ? <><span>⏳</span><span>Generating PDF...</span></> : <><span>⬇️</span><span>Download as PDF</span></>}
                </button>
              </div>
              {activeIsActive && (
                <div style={{ marginBottom: '15px', padding: '12px 15px', backgroundColor: '#dbeafe', border: '2px solid #3b82f6', borderRadius: '8px' }}>
                  <p style={{ margin: 0, color: '#1e3a8a', fontSize: '14px' }}>
                    🔒 <strong>Locked for current audit cycle</strong> · Generated {new Date(activeRecord.generated_at).toLocaleDateString()} · Regenerate available after {new Date(activeRecord.expires_at).toLocaleDateString()}
                  </p>
                </div>
              )}
              {activeIsArchived && (
                <div style={{ marginBottom: '15px', padding: '12px 15px', backgroundColor: '#fef3c7', border: '2px solid #f59e0b', borderRadius: '8px' }}>
                  <p style={{ margin: 0, color: '#854d0e', fontSize: '14px' }}>
                    ⏰ <strong>Archived</strong> — last generated {new Date(activeRecord.generated_at).toLocaleDateString()}. Click <strong>🔄 Regenerate</strong> next to this item to create a fresh one for the new audit cycle.
                  </p>
                </div>
              )}
              <div
                style={{ backgroundColor: '#f8fafc', padding: '20px', borderRadius: '10px', border: '2px solid #e2e8f0', fontFamily: 'Inter, Helvetica, sans-serif', fontSize: '15px', lineHeight: '1.8', maxHeight: '500px', overflowY: 'auto', color: '#1e293b' }}
                dangerouslySetInnerHTML={{ __html: renderAiMarkdown(generatedDoc) }}
              />
              <div style={{ marginTop: '15px', padding: '15px', backgroundColor: '#dcfce7', border: '2px solid #22c55e', borderRadius: '10px' }}>
                <p style={{ margin: 0, color: '#15803d', fontSize: '14px' }}>
                  <strong>✨ This document satisfies requirements from:</strong> ISO/IEC 42001 ({current.iso.reference}), EU AI Act ({current.eu.reference}), and NIST AI RMF ({current.nist.reference})
                </p>
              </div>
            </div>
          )}

          {/* Evidence Needed */}
          <div style={{ backgroundColor: '#fef3c7', padding: '25px', borderRadius: '15px', border: '2px solid #eab308' }}>
            <h3 style={{ fontSize: '20px', fontWeight: 'bold', marginBottom: '15px', color: '#854d0e' }}>📋 Evidence/Documentation Needed:</h3>
            <ul style={{ margin: 0, paddingLeft: '20px', color: '#92400e' }}>
              {current.evidence.map((item, idx) => <li key={idx} style={{ marginBottom: '8px', lineHeight: '1.5', fontSize: '15px' }}>{item}</li>)}
            </ul>
          </div>
        </div>
      </div>

      {/* Efficiency Tips */}
      <div style={{ backgroundColor: 'white', padding: '30px', borderRadius: '20px', boxShadow: '0 4px 6px rgba(0,0,0,0.07)' }}>
        <h2 style={{ fontSize: '28px', fontWeight: 'bold', marginBottom: '20px', color: '#0f172a' }}>💡 Efficiency Tips</h2>
        <div style={{ display: 'grid', gap: '15px' }}>
          {[
            { tip: 'Use a single risk register', detail: 'for all frameworks - just map risks to specific requirements in each framework' },
            { tip: 'Create modular documentation', detail: 'that can be assembled for different audiences (auditors, regulators, internal teams)' },
            { tip: 'Implement a unified monitoring dashboard', detail: 'tracking metrics for all frameworks simultaneously' },
            { tip: 'Conduct single impact assessment', detail: 'covering ISO, EU, and NIST requirements instead of three separate assessments' },
            { tip: 'Use cross-functional teams', detail: 'to avoid siloed compliance efforts - one team, one plan, three frameworks satisfied' },
          ].map(({ tip, detail }, i) => (
            <div key={i} style={{ display: 'flex', gap: '15px', alignItems: 'start' }}>
              <span style={{ fontSize: '24px' }}>✅</span>
              <p style={{ margin: 0, color: '#334155', lineHeight: '1.6' }}>
                <strong>{tip}</strong> {detail}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Footer disclaimer */}
      <div style={{ marginTop: '32px', padding: '14px 16px', backgroundColor: '#fef3c7', border: '2px solid #fbbf24', borderRadius: '10px' }}>
        <p style={{ margin: 0, color: '#92400e', fontSize: '13px' }}>
          <strong>⚠️ Disclaimer:</strong>  AI‑generated compliance guidance – not legal advice. Always verify with a qualified professional. For questions, contact QuantumAIshield support. Blockchain records are permanent – please confirm before sealing.
        </p>
      </div>

    </div>
  );
}

export default CrossFrameworkMapper;